import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CmsLayoutComponent } from '@core/main-layout/cms/cms-layout/cms-layout.component';
import { LayoutComponent } from '@core/main-layout/layout/layout.component';
import { AuthService } from '@services/auth/auth.service';
import { MetaService } from '@services/meta.service';
import { NativeService } from '@services/native/native.service';
import { ThemeService } from '@services/theme.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.less'],
	standalone: true,
	imports: [RouterOutlet, CmsLayoutComponent, LayoutComponent, TranslateModule],
})
export class AppComponent {
	private readonly _r = inject(Router);
	private readonly _auth = inject(AuthService);
	readonly meta = inject(MetaService);
	private readonly _native = inject(NativeService);
	/**
	 * Instantiated HERE, at the root of the app, so the picked skin is applied on EVERY page.
	 * Until now the only injectors were the CMS header/sidenav and the chart, i.e. logged-in views —
	 * the login screen relied solely on the pre-paint script in index.html, and any theme that script
	 * did not know about silently fell back to the default one.
	 */
	readonly theme = inject(ThemeService);
	private readonly _translate = inject(TranslateService);

	isSpecialPage = signal<boolean>(false);
	isCms = signal<boolean>(false);

	/**
	 * Powłoka aplikacji (pasek nawigacji, sidenav) tylko dla ZALOGOWANEGO.
	 *
	 * Sama lista `specialPages` tego nie pilnowała: wyłapywała adresy logowania, oferty czy
	 * akademii, ale każdy adres SPOZA listy — a więc i strona 404, na którą wpada wildcard —
	 * szedł gałęzią CMS. Wylogowany gość po wejściu na nieistniejący adres (stara zakładka,
	 * literówka, martwy link) dostawał pełny pasek nawigacji na dole ekranu.
	 *
	 * Bramka na sesji zamyka całą tę klasę błędów naraz, a nie jeden adres: trasy chronione
	 * i tak przepuszcza `authGuard`, więc jedyne, co tu wpadało bez sesji, to właśnie adresy
	 * niechronione i nieujęte na liście.
	 */
	readonly showCmsShell = computed(() => this.isCms() && this._auth.isAuthenticated());

	/** Read-only banner visibility — persisted to localStorage so it stays
	 * dismissed across reloads. Restored via the small floating pill. */
	private static readonly RO_BANNER_KEY = 'cryptionis.roBanner.dismissed';
	bannerVisible = signal<boolean>(
		typeof localStorage === 'undefined' ||
			localStorage.getItem(AppComponent.RO_BANNER_KEY) !== '1',
	);

	dismissBanner(): void {
		this.bannerVisible.set(false);

		try {
			localStorage.setItem(AppComponent.RO_BANNER_KEY, '1');
		} catch {
			/* storage may be unavailable in some browsers/iframes */
		}
	}

	showBanner(): void {
		this.bannerVisible.set(true);

		try {
			localStorage.removeItem(AppComponent.RO_BANNER_KEY);
		} catch {
			/* ignore */
		}
	}

	private specialPages: RegExp[] = [
		/^\/login/,
		/^\/alert-film\//,
		/^\/forgot-password/,
		/^\/register/,
		/^\/verify-email/,
		/^\/offer/,
		/^\/privacy/,
		/^\/account-deletion/,
	];

	private readonly navigationEnd = toSignal(
		this._r.events.pipe(
			filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
			takeUntilDestroyed(),
		),
	);

	constructor() {
		// Native (Capacitor) shell setup — status bar, Android back, hide splash. No-op on web.
		void this._native.init();

		// Dismiss the native boot splash (defined in index.html: green tech animation + logo/wordmark
		// fade-in). It shows instantly with the page; here we let it play, then fade it out and remove
		// it once Angular is up. No-op on web (the element stays display:none there).
		if (Capacitor.isNativePlatform()) {
			const boot = document.getElementById('boot-splash');
			if (boot) {
				setTimeout(() => boot.classList.add('bs-hide'), 1900);
				setTimeout(() => boot.remove(), 2400);
			}
		}

		// Język portalu w `<html lang>` — czytniki ekranu i wykresy (`helpers/lw-chart.ts`: nazwy
		// miesięcy na osi czasu) biorą go stąd, a nie z języka przeglądarki.
		this._translate.onLangChange
			.pipe(takeUntilDestroyed())
			.subscribe(({ lang }) => document.documentElement.setAttribute('lang', lang));

		// Fetch runtime feature flags once at app boot (read-only banner, etc.)
		this.meta.load().subscribe();

		effect(() => {
			const route = this.navigationEnd();

			if (route) {
				this.isCms.set(true);
				this.isSpecialPage.set(false);

				for (const pattern of this.specialPages) {
					if (pattern.test(route.urlAfterRedirects)) {
						this.isSpecialPage.set(true);
						break;
					}
				}
			}
		});
	}

	ngOnInit(): void {
		this.handleNotifications();
		// `meta.load()` already fires in the constructor — calling it again here duplicated the
		// runtime-flags request on every page load. Removed.
	}

	handleNotifications(): void {
		// TODO: Implement push notifications when needed
	}
}
